import { TaxProvider } from '@/models/settings.model';
import { Color } from 'antd/es/color-picker';

export enum SettingsForms {
  GENERAL = 'general',
  UI = 'ui',
  BULK_GENERAL = 'bulkGeneral',
}

export enum GeneralSettingsBulkFormFields {
  GOOGLE_TAG_MANAGER = 'checkboxGoogleTagManagement',
  COPYRIGHT = 'checkboxCopyright',
  CHANNEL = 'checkboxChannel',
  SHIPPING = 'checkboxShipping',
  SMTP_HOST = 'checkboxSmtpHost',
  SMTP_PORT = 'checkboxSmtpPort',
  SMTP_AUTH_USER = 'checkboxSmtpAuthUser',
  SMTP_AUTH_PASS = 'checkboxSmtpAuthPass',
  PHONES_FOOTER = 'checkboxPhonesFooter',
  PHONES_CONTACT_US = 'checkboxPhonesContactUs',
  ADMIN_EMAILS = 'checkboxAdminEmails',
  POPULAR_PRODUCTS = 'checkboxPopularProducts',
  HOME_PAGE_BLOG_POST = 'checkboxHomePageBlogPost',
  POPULAR_CATEGORIES = 'checkboxPopularCategories',
  TOP_PICKS_OF_THE_MONTH = 'checkboxTopPicksOfTheMonth',
  CONTACT_INFO_EMAIL = 'checkboxContactInfoEmail',
  CONTACT_INFO_WORKING_HOURS = 'checkboxContactInfoWorkingHours',
  CONTACT_INFO_ADDRESS = 'checkboxContactInfoAddress',
  SUPPORT_INFO_NAME = 'checkboxSupportInfoName',
  SUPPORT_INFO_PHONE = 'checkboxSupportInfoPhone',
  SUPPORT_INFO_EMAIL = 'checkboxSupportInfoEmail',
  MIN_ORDER_ENABLED = 'checkboxMinOrderEnabled',
  MIN_ORDER_AMOUNT = 'checkboxMinOrderAmount',
  MIN_ORDER_POPUP_TITLE = 'checkboxMinOrderPopupTitle',
  MIN_ORDER_POPUP_DESCRIPTION = 'checkboxMinOrderPopupDescription',
}

export enum GeneralSettingsFormFields {
  GOOGLE_TAG_MANAGER = 'googleTagManagement',
  COPYRIGHT = 'copyright',
  NEW_MACHINES_CATEGORY = 'newMachinesCategory',
  MONTHLY_SPECIAL_CATEGORY = 'monthlySpecialCategory',
  CHANNEL = 'channel',
  SHIPPING = 'shipping',
  SMTP_HOST = 'smtpHost',
  SMTP_PORT = 'smtpPort',
  SMTP_AUTH_USER = 'smtpAuthUser',
  SMTP_AUTH_PASS = 'smtpAuthPass',
  PHONES_FOOTER = 'phonesFooter',
  PHONES_CONTACT_US = 'phonesContactUs',
  ADMIN_EMAILS = 'adminEmails',
  POPULAR_PRODUCTS = 'popularProducts',
  HOME_PAGE_BLOG_POST = 'homePageBlogPost',
  POPULAR_CATEGORIES = 'popularCategories',
  TOP_PICKS_OF_THE_MONTH = 'topPicksOfTheMonth',
  CONTACT_INFO_EMAIL = 'contactInfoEmail',
  CONTACT_INFO_WORKING_HOURS = 'contactInfoWorkingHours',
  CONTACT_INFO_ADDRESS = 'contactInfoAddress',
  SUPPORT_INFO_NAME = 'supportInfoName',
  SUPPORT_INFO_PHONE = 'supportInfoPhone',
  SUPPORT_INFO_EMAIL = 'supportInfoEmail',
  META_OG_SITE_NAME = 'ogSiteName',
  AUTHOR = 'author',
  MIN_ORDER_ENABLED = 'minOrderEnabled',
  MIN_ORDER_AMOUNT = 'minOrderAmount',
  MIN_ORDER_POPUP_TITLE = 'minOrderPopupTitle',
  MIN_ORDER_POPUP_DESCRIPTION = 'minOrderPopupDescription',
  TAX_PROVIDER = 'taxProvider',
}

export enum UISettingsFormFields {
  HEADER_LOGO = 'headerLogo',
  FOOTER_LOGO = 'footerLogo',
  EMPTY_STATE_LOGO = 'emptyStateLogo',
  MOBILE_HEADER_LOGO = 'mobileHeaderLogo',
  COMPANY_LOGO = 'companyLogo',
  FAVICON48 = 'favicon48',
  FAVICON96 = 'favicon96',
  FAVICON144 = 'favicon144',
  PRIMARY_COLOR = 'primaryColor',
  PRIMARY_HOVER_COLOR = 'primaryHoverColor',
  DISABLED_COLOR = 'disabledColor',
  COLOR_ON_PRIMARY = 'colorOnPrimary',
  SECONDARY_COLOR = 'secondaryColor',
  COLOR_ON_SECONDARY = 'colorOnSecondary',
  ALT_HEADER_LOGO = 'headerLogoAlt',
  ALT_FOOTER_LOGO = 'footerLogoAlt',
  ALT_EMPTY_STATE_LOGO = 'emptyStateLogoAlt',
  ALT_MOBILE_HEADER_LOGO = 'mobileHeaderLogoAlt',
  ALT_COMPANY_LOGO = 'companyLogoAlt',
  ALT_FAVICON48 = 'favicon48Alt',
  ALT_FAVICON96 = 'favicon96Alt',
  ALT_FAVICON144 = 'favicon144Alt',
  TITLE_HEADER_LOGO = 'headerLogoTitle',
  TITLE_FOOTER_LOGO = 'footerLogoTitle',
  TITLE_MOBILE_HEADER_LOGO = 'mobileHeaderLogoTitle',
}

export type TUISettingsFormValues = {
  [UISettingsFormFields.HEADER_LOGO]: File | string;
  [UISettingsFormFields.ALT_HEADER_LOGO]: string;
  [UISettingsFormFields.TITLE_HEADER_LOGO]: string;
  [UISettingsFormFields.FOOTER_LOGO]: File | string;
  [UISettingsFormFields.ALT_FOOTER_LOGO]: string;
  [UISettingsFormFields.TITLE_FOOTER_LOGO]: string;
  [UISettingsFormFields.EMPTY_STATE_LOGO]: File | string;
  [UISettingsFormFields.ALT_EMPTY_STATE_LOGO]: string;
  [UISettingsFormFields.MOBILE_HEADER_LOGO]: File | string;
  [UISettingsFormFields.ALT_MOBILE_HEADER_LOGO]: string;
  [UISettingsFormFields.TITLE_MOBILE_HEADER_LOGO]: string;
  [UISettingsFormFields.COMPANY_LOGO]: File | string;
  [UISettingsFormFields.ALT_COMPANY_LOGO]: string;
  [UISettingsFormFields.FAVICON48]: File | string;
  [UISettingsFormFields.ALT_FAVICON48]: string;
  [UISettingsFormFields.FAVICON96]: File | string;
  [UISettingsFormFields.ALT_FAVICON96]: string;
  [UISettingsFormFields.FAVICON144]: File | string;
  [UISettingsFormFields.ALT_FAVICON144]: string;
  [UISettingsFormFields.PRIMARY_COLOR]: Color | string;
  [UISettingsFormFields.PRIMARY_HOVER_COLOR]: Color | string;
  [UISettingsFormFields.DISABLED_COLOR]: Color | string;
  [UISettingsFormFields.COLOR_ON_PRIMARY]: Color | string;
  [UISettingsFormFields.SECONDARY_COLOR]: Color | string;
  [UISettingsFormFields.COLOR_ON_SECONDARY]: Color | string;
};

export type TGeneralSettingsFormValues = {
  [GeneralSettingsFormFields.GOOGLE_TAG_MANAGER]: string;
  [GeneralSettingsFormFields.ADMIN_EMAILS]: string[];
  [GeneralSettingsFormFields.COPYRIGHT]: string;
  [GeneralSettingsFormFields.NEW_MACHINES_CATEGORY]: string;
  [GeneralSettingsFormFields.MONTHLY_SPECIAL_CATEGORY]: string;
  [GeneralSettingsFormFields.CHANNEL]: string;
  [GeneralSettingsFormFields.SMTP_AUTH_PASS]: string;
  [GeneralSettingsFormFields.SMTP_AUTH_USER]: string;
  [GeneralSettingsFormFields.SMTP_HOST]: string;
  [GeneralSettingsFormFields.SMTP_PORT]: number;
  [GeneralSettingsFormFields.PHONES_FOOTER]: string;
  [GeneralSettingsFormFields.PHONES_CONTACT_US]: string;
  [GeneralSettingsFormFields.POPULAR_PRODUCTS]: string[];
  [GeneralSettingsFormFields.HOME_PAGE_BLOG_POST]: string;
  [GeneralSettingsFormFields.POPULAR_CATEGORIES]: string[];
  [GeneralSettingsFormFields.TOP_PICKS_OF_THE_MONTH]: string[];
  [GeneralSettingsFormFields.CONTACT_INFO_EMAIL]: string;
  [GeneralSettingsFormFields.CONTACT_INFO_WORKING_HOURS]: string;
  [GeneralSettingsFormFields.CONTACT_INFO_ADDRESS]: string;
  [GeneralSettingsFormFields.SUPPORT_INFO_NAME]: string;
  [GeneralSettingsFormFields.SUPPORT_INFO_PHONE]: string;
  [GeneralSettingsFormFields.SUPPORT_INFO_EMAIL]: string;
  [GeneralSettingsFormFields.META_OG_SITE_NAME]: string;
  [GeneralSettingsFormFields.AUTHOR]: string;
  [GeneralSettingsFormFields.MIN_ORDER_ENABLED]: boolean;
  [GeneralSettingsFormFields.MIN_ORDER_AMOUNT]: number;
  [GeneralSettingsFormFields.MIN_ORDER_POPUP_TITLE]: string;
  [GeneralSettingsFormFields.MIN_ORDER_POPUP_DESCRIPTION]: string;
  [GeneralSettingsFormFields.TAX_PROVIDER]: TaxProvider;
};

export type TGeneralSettingsBulkFormValues = Omit<TGeneralSettingsFormValues, GeneralSettingsFormFields.CHANNEL> & {
  [GeneralSettingsFormFields.CHANNEL]: number[];
  [GeneralSettingsBulkFormFields.GOOGLE_TAG_MANAGER]: boolean;
  [GeneralSettingsBulkFormFields.ADMIN_EMAILS]: boolean;
  [GeneralSettingsBulkFormFields.COPYRIGHT]: boolean;
  [GeneralSettingsBulkFormFields.SMTP_AUTH_PASS]: boolean;
  [GeneralSettingsBulkFormFields.SMTP_AUTH_USER]: boolean;
  [GeneralSettingsBulkFormFields.SMTP_HOST]: boolean;
  [GeneralSettingsBulkFormFields.SMTP_PORT]: boolean;
  [GeneralSettingsBulkFormFields.PHONES_FOOTER]: boolean;
  [GeneralSettingsBulkFormFields.PHONES_CONTACT_US]: boolean;
  [GeneralSettingsBulkFormFields.POPULAR_PRODUCTS]: boolean;
  [GeneralSettingsBulkFormFields.HOME_PAGE_BLOG_POST]: boolean;
  [GeneralSettingsBulkFormFields.POPULAR_CATEGORIES]: boolean;
  [GeneralSettingsBulkFormFields.TOP_PICKS_OF_THE_MONTH]: boolean;
  [GeneralSettingsBulkFormFields.CONTACT_INFO_EMAIL]: boolean;
  [GeneralSettingsBulkFormFields.CONTACT_INFO_WORKING_HOURS]: boolean;
  [GeneralSettingsBulkFormFields.CONTACT_INFO_ADDRESS]: boolean;
  [GeneralSettingsBulkFormFields.SUPPORT_INFO_NAME]: boolean;
  [GeneralSettingsBulkFormFields.SUPPORT_INFO_PHONE]: boolean;
  [GeneralSettingsBulkFormFields.SUPPORT_INFO_EMAIL]: boolean;
  [GeneralSettingsBulkFormFields.MIN_ORDER_ENABLED]: boolean;
  [GeneralSettingsBulkFormFields.MIN_ORDER_AMOUNT]: boolean;
  [GeneralSettingsBulkFormFields.MIN_ORDER_POPUP_TITLE]: boolean;
  [GeneralSettingsBulkFormFields.MIN_ORDER_POPUP_DESCRIPTION]: boolean;
};
